import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useMutation } from '@tanstack/react-query';
import { uploadFile } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const Upload = () => {
  const { user } = useAuth();
  const [uploadType, setUploadType] = useState('routes');
  const [uploadResults, setUploadResults] = useState(null);

  const uploadMutation = useMutation(uploadFile, {
    onSuccess: (data) => {
      setUploadResults(data);
    },
    onError: (error) => {
      console.error('Upload failed:', error);
      setUploadResults({
        success: false,
        error: error.response?.data?.message || 'Upload failed'
      });
    }
  });

  const onDrop = (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;

    const file = acceptedFiles[0];
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', uploadType);

    uploadMutation.mutate(formData);
    setUploadResults(null);
  };

  const { getRootProps, getInputProps, isDragActive, acceptedFiles } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    },
    maxFiles: 1,
    multiple: false
  });

  const uploadTypes = [
    { value: 'routes', label: 'Routes Data', description: 'Upload route information (origin, destination, distance)' },
    { value: 'schedules', label: 'Schedules Data', description: 'Upload schedule information (times, prices, capacity)' },
    { value: 'customers', label: 'Customer Data', description: 'Upload customer information (name, phone, email)' }
  ];

  const getExampleFormat = (type) => {
    switch (type) {
      case 'routes':
        return 'name, origin, destination, distance_km, duration_minutes';
      case 'schedules':
        return 'route_name, departure_time, arrival_time, days_of_week, adult_price, child_price, senior_price, max_capacity';
      case 'customers':
        return 'first_name, last_name, phone, email, date_of_birth, emergency_contact_name, emergency_contact_phone';
      default:
        return '';
    }
  };

  // Only allow admin users to upload data
  if (user?.role !== 'admin') {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">Admin Access Required</h3>
              <p className="mt-1 text-sm text-yellow-700">
                You need administrator privileges to access the data upload functionality.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Data Upload</h1>
        <p className="mt-2 text-gray-600">
          Upload CSV or Excel files to bulk import data into the system.
        </p>
      </div>

      {/* Upload Type Selection */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Select Upload Type</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {uploadTypes.map((type) => (
            <label
              key={type.value}
              className={`relative flex cursor-pointer rounded-lg border p-4 focus:outline-none ${
                uploadType === type.value
                  ? 'border-blue-500 ring-2 ring-blue-500 bg-blue-50'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              <input
                type="radio"
                name="uploadType"
                value={type.value}
                checked={uploadType === type.value}
                onChange={(e) => setUploadType(e.target.value)}
                className="sr-only"
              />
              <div className="flex-1">
                <div className="font-medium text-gray-900">{type.label}</div>
                <div className="text-sm text-gray-500 mt-1">{type.description}</div>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* File Format Information */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <h3 className="text-sm font-medium text-blue-800 mb-2">Expected Format for {uploadTypes.find(t => t.value === uploadType)?.label}</h3>
        <p className="text-sm text-blue-700 font-mono bg-white px-2 py-1 rounded">
          {getExampleFormat(uploadType)}
        </p>
        <p className="text-xs text-blue-600 mt-2">
          • First row should contain column headers<br/>
          • Supported formats: CSV, XLS, XLSX<br/>
          • Maximum file size: 10MB
        </p>
      </div>

      {/* Upload Area */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
            isDragActive
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-300 hover:border-gray-400'
          }`}
        >
          <input {...getInputProps()} />
          <svg
            className="mx-auto h-12 w-12 text-gray-400 mb-4"
            stroke="currentColor"
            fill="none"
            viewBox="0 0 48 48"
          >
            <path
              d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>

          {isDragActive ? (
            <p className="text-lg text-blue-600 font-medium">Drop the file here</p>
          ) : (
            <>
              <p className="text-lg text-gray-600 mb-2">
                Drag and drop your file here, or <span className="text-blue-600 font-medium">browse</span>
              </p>
              <p className="text-sm text-gray-500">CSV, XLS, or XLSX files only</p>
            </>
          )}
        </div>

        {acceptedFiles.length > 0 && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <h4 className="text-sm font-medium text-gray-900 mb-2">Selected File:</h4>
            <div className="text-sm text-gray-600">
              {acceptedFiles[0].name} ({(acceptedFiles[0].size / 1024).toFixed(1)} KB)
            </div>
          </div>
        )}
      </div>

      {/* Upload Status */}
      {uploadMutation.isLoading && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <svg className="animate-spin h-5 w-5 text-blue-500 mr-3" viewBox="0 0 24 24">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
                fill="none"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span className="text-blue-700">Processing upload...</span>
          </div>
        </div>
      )}

      {/* Upload Results */}
      {uploadResults && (
        <div className={`border rounded-lg p-4 ${
          uploadResults.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
        }`}>
          <div className="flex items-start">
            {uploadResults.success ? (
              <svg className="h-5 w-5 text-green-500 mt-0.5 mr-3" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg className="h-5 w-5 text-red-500 mt-0.5 mr-3" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            )}
            <div className="flex-1">
              <h3 className={`font-medium ${
                uploadResults.success ? 'text-green-800' : 'text-red-800'
              }`}>
                {uploadResults.success ? 'Upload Successful' : 'Upload Failed'}
              </h3>

              {uploadResults.success && uploadResults.summary && (
                <div className="mt-2 text-sm text-green-700">
                  <p>Successfully processed {uploadResults.summary.total} records:</p>
                  <ul className="list-disc list-inside mt-1">
                    <li>{uploadResults.summary.created} new records created</li>
                    {uploadResults.summary.updated > 0 && (
                      <li>{uploadResults.summary.updated} records updated</li>
                    )}
                    {uploadResults.summary.skipped > 0 && (
                      <li>{uploadResults.summary.skipped} records skipped</li>
                    )}
                  </ul>
                </div>
              )}

              {uploadResults.error && (
                <p className="mt-1 text-sm text-red-700">{uploadResults.error}</p>
              )}

              {uploadResults.errors && uploadResults.errors.length > 0 && (
                <div className="mt-2">
                  <p className="text-sm text-red-700 font-medium">Errors found:</p>
                  <ul className="text-sm text-red-600 mt-1 max-h-40 overflow-y-auto">
                    {uploadResults.errors.slice(0, 10).map((error, index) => (
                      <li key={index} className="font-mono text-xs">Row {error.row}: {error.message}</li>
                    ))}
                    {uploadResults.errors.length > 10 && (
                      <li className="text-sm">... and {uploadResults.errors.length - 10} more errors</li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Upload;